import { useEffect, useState, type ReactElement } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "professionals-pwa-dismissed";

export function PWAInstallBanner(): ReactElement | null {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!deferredPrompt || dismissed) return null;

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setDeferredPrompt(null);
  }
  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: "calc(70px + env(safe-area-inset-bottom))",
        left: "50%",
        transform: "translateX(-50%)",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "0.75rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        zIndex: 1000,
        boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
        maxWidth: "calc(100vw - 2rem)",
      }}
      role="dialog"
      aria-label="Install app"
    >
      <span style={{ fontSize: "0.875rem", color: "var(--text)" }}>
        Install Professionals for a faster experience
      </span>
      <button className="btn-primary" style={{ padding: "0.4rem 0.75rem", fontSize: "0.875rem" }} onClick={handleInstall}>
        Install
      </button>
      <button
        onClick={handleDismiss}
        style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "1.25rem", padding: "0.25rem 0.5rem" }}
        aria-label="Not now"
      >
        ×
      </button>
    </div>
  );
}
